import { TrashIcon } from '@primer/octicons-react';
import { ActionList, Box, Checkbox, FormControl, IconButton, Link } from '@primer/react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../components/ui/button';
import Modal from '../../../components/ui/modal';
import ModalActions from '../../../components/ui/modal/ModalActions';
import Text from '../../../components/ui/text';
import TextAreaAutoHeight from '../../../components/ui/text-area-auto-height';
import TextInput from '../../../components/ui/text-input';
import Upload from '../../../components/upload';
import { useCommonContext } from '../../../contexts/common';
import { ePageBaseStatus } from '../../../models/ePageBaseStatus';
import { ILegalDocument } from '../../../models/response/legal-document/ILegalDocument';
import { IUploadRespone } from '../../../models/response/upload/IUploadRespone';
import { actions } from '../../../state/actions/actionsWrapper';
import { RootState } from '../../../state/reducers';
import ComboboxLegalGroup from '../../../components/combobox-legal-group';

const LegalDocumentEditModal = () => {
    const { status, documentEditing } = useSelector((x: RootState) => x.legalDocument.documents)

    const dispatch = useDispatch();
    const { translate } = useCommonContext();
    const { register, handleSubmit, clearErrors, reset, setError, control, formState: { errors } } = useForm<ILegalDocument>({
        defaultValues: {
            ...documentEditing
        }
    })
    const onSubmit = (data: ILegalDocument) => {
        dispatch(actions.legalDocument.document.SAVE_START({
            ...data,
            id: documentEditing?.id ?? 0
        }))

    }
    return (
        <Modal
            title={documentEditing ? translate(`Base.Label.Update`) : translate(`Base.Label.AddNew`)}
            isOpen
            onClose={() => {
                dispatch(actions.legalDocument.document.CLOSE_EDIT_MODAL(undefined))
            }}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{
                    display: "grid",
                    gap: 2
                }}>
                    <Controller
                        control={control}
                        name="sis_legal_group_id"
                        rules={{
                            required: {
                                value: true,
                                message: ""
                            }
                        }}
                        render={({ field }) => (
                            <>
                                <FormControl>
                                    <FormControl.Label><Text text='LegalDocument.Group' /></FormControl.Label>
                                    <ComboboxLegalGroup
                                        value={field.value}
                                        onValueChanged={(id) => {
                                            field.onChange(id)
                                        }}
                                    />
                                </FormControl>
                                {
                                    errors && errors["sis_legal_group_id"] &&
                                    <FormControl.Validation variant="error">
                                        <>{`${translate(`Base.Form.RequiredMessage`)} ${translate(`LegalDocument.Group`)}`}</>
                                    </FormControl.Validation>
                                }
                            </>
                        )}
                    />
                    <FormControl>
                        <FormControl.Label><Text text='LegalDocument.Name' /></FormControl.Label>
                        <TextInput
                            block
                            errors={errors}
                            register={register}
                            required={true}
                            name='name'
                            validateMessage={`${translate(`Base.Form.RequiredMessage`)} ${translate(`LegalDocument.Name`)}`}
                        />
                    </FormControl>
                    <FormControl>
                        <FormControl.Label><Text text='LegalDocument.NameEn' /></FormControl.Label>
                        <TextInput
                            block
                            errors={errors}
                            register={register}
                            required={true}
                            name='name_en'
                            validateMessage={`${translate(`Base.Form.RequiredMessage`)} ${translate(`LegalDocument.NameEn`)}`}
                        />
                    </FormControl>

                    <Controller
                        control={control}
                        name="url"
                        rules={{
                            required: {
                                value: true,
                                message: ""
                            }
                        }}
                        render={({ field }) => (
                            <>
                                <FormControl>
                                    <FormControl.Label><Text text='LegalDocument.FileUrl' /></FormControl.Label>
                                    {!field.value &&
                                        <Upload
                                            accept={{
                                                'image/*': [],
                                                'application/pdf': [],
                                                'application/msword': ['.doc', '.docx'],
                                                'application/vnd.ms-excel': ['.xls', '.xlsx'],
                                                'application/msg': ['.msg'],
                                            }}
                                            sx={{ width: "100%" }}
                                            onUploadSuccess={(data: IUploadRespone) => {
                                                console.log({
                                                    upload: data
                                                });
                                                field.onChange(data.url)
                                            }}
                                        />
                                    }
                                    {field.value &&
                                        <ActionList variant='full' sx={{ width: "100%" }}>
                                            <ActionList.Item>
                                                <Link target='_blank' href={field.value}>
                                                    {field.value}
                                                </Link>
                                                <ActionList.TrailingVisual>
                                                    <IconButton icon={TrashIcon} aria-label='' variant='invisible'
                                                        size='small'
                                                        sx={{
                                                            m: -1
                                                        }}
                                                        onClick={() => {
                                                            field.onChange("")
                                                        }}
                                                    />
                                                </ActionList.TrailingVisual>
                                            </ActionList.Item>
                                        </ActionList>
                                    }
                                </FormControl>
                                {
                                    errors && errors["url"] &&
                                    <FormControl.Validation variant="error">
                                        <>{`${translate(`Base.Form.RequiredMessage`)} ${translate(`LegalDocument.FileUrl`)}`}</>
                                    </FormControl.Validation>
                                }
                            </>
                        )}
                    />

                    <Controller
                        control={control}
                        name="description"
                        rules={{
                            required: {
                                value: false,
                                message: ""
                            }
                        }}
                        render={({ field }) => (
                            <>
                                <FormControl>
                                    <FormControl.Label><Text text='LegalDocument.Description' /></FormControl.Label>
                                    <TextAreaAutoHeight
                                        block

                                        placeholder={translate(`LegalDocument.Description.PlaceHolder`)}
                                        defaultValue={field.value}
                                        onValueChanged={(value) => {
                                            field.onChange(value)
                                        }}
                                    />
                                </FormControl>
                            </>
                        )}
                    />
                    <Controller
                        control={control}
                        name="is_active"
                        rules={{
                            required: {
                                value: false,
                                message: ""
                            }
                        }}
                        render={({ field }) => (
                            <>
                                <FormControl>
                                    <FormControl.Label><Text text='LegalDocument.IsActive' /></FormControl.Label>
                                    <Checkbox checked={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.checked)
                                        }}
                                    />
                                </FormControl>
                            </>
                        )}
                    />

                </Box>
                <ModalActions>
                    <Button text='Base.Label.Close'
                        size='medium'
                        onClick={() => {
                            dispatch(actions.legalDocument.document.CLOSE_EDIT_MODAL(undefined))
                        }}
                    />
                    <Button text={documentEditing ? translate(`Base.Label.Update`) : translate(`Base.Label.AddNew`)}
                        variant='primary'
                        type='submit'
                        size='medium'
                        isLoading={status === ePageBaseStatus.is_saving}
                    />
                </ModalActions>
            </form>
        </Modal>
    );
};

export default LegalDocumentEditModal;